import React from 'react';
import PropTypes from 'prop-types';
import useDataSourceContext from '../../useDataSourceContext';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { FormattedMessage } from 'react-intl';
import Filter from '../../../FiltersField/Filter';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Actions from './Actions';
import Header from './Header';
import HeaderNew from './HeaderNew';
import Icon from '../../../Icon';
import ItemContainer from './ItemContainer';
import * as defaultRenderers from '../../../FiltersField/renderers';
import translations from './translations';

const FilterSelectionItem = ({ filter, filterFormatters, filterRenderers, isOpen }) => {
  const intl = useIntl();
  const { filters: filtersModule = {} } = useDataSourceContext();
  const {
    createFilterSelection,
    clearFilterSelection,
    filterSelection,
    setFilterSelectionKey,
    setFilterSelectionOperator,
    setFilterSelectionValue,
    loadFilterSelection,
    saveFilterSelection,
    getAvailableFilters,
    getFilterSelectionOperators,
    getFilterName,
    getFilterType,
    removeActiveFilter,
  } = filtersModule;
  const renderers = { ...defaultRenderers.default, ...filterRenderers };

  /**
   * Handle the submit of the filter selection form.
   * @param {Event} event
   * @returns {boolean}
   */
  const handleSubmit = event => {
    saveFilterSelection(filter?.key);

    event.preventDefault();
    event.stopPropagation();
    return false;
  };

  /**
   * Handles closing and clearing the filter selection.
   */
  const handleCloseFilterSelection = () => {
    clearFilterSelection();
  };

  /**
   * Handles loading the filter selection based on the current filter.
   */
  const handleLoadFilterSelection = () => {
    if (!filter.isNew) {
      loadFilterSelection(filter.key);
    } else {
      createFilterSelection();
    }
  };

  /**
   * Remove the active filter if exists and clear the filter selection.
   */
  const handleRemoveFilterSelection = () => {
    if (!filter.isNew) {
      removeActiveFilter(filter.key);
    }

    clearFilterSelection();
  };

  const handleChangeFilterKey = key => {
    if (filterSelection?.key === key) {
      return;
    }

    setFilterSelectionKey(key);
  };

  const handleChangeOperator = operator => {
    if (filterSelection?.operator === operator) {
      return;
    }

    setFilterSelectionOperator(operator);
  };

  const handleChangeValues = value => {
    setFilterSelectionValue(value);
  };

  const handleChangeSelection = (name, ev, value) => {
    switch (name) {
      case 'key':
        handleChangeFilterKey(value);
        break;
      case 'operator':
        handleChangeOperator(value);
        break;
      case 'values':
        handleChangeValues(value);
        break;
      default:
        break;
    }
  };

  const handleFetchOperators = filterKey => {
    const operators = getFilterSelectionOperators(filterKey);

    return operators.reduce(
      (acc, operator) => [
        ...acc,
        {
          label: intl.formatMessage({
            id: `dataGrid.filterOperators.${operator}`,
            defaultMessage: operator,
            description: `Label of the filter operator ${operator}`,
          }),
          value: operator,
        },
      ],
      [],
    );
  };

  const handleFetchFormat = filterKey => getFilterType(filterKey);

  const handleFetchSuggestions = () => filterSelection?.filterOptions ?? [];

  const handleFetchValueProps = filterKey => {
    const format = handleFetchFormat(filterKey);

    if (format === 'boolean') {
      return { changeOnMounted: false };
    }

    return {};
  };

  const getFilterDisplayValue = filterData => {
    const { name, operator, value, type: filterType } = filterData;
    const ValueRenderer = renderers[filterType] ?? renderers.default;
    return <ValueRenderer name={name} operator={operator} value={value} />;
  };

  const availableFilters = getAvailableFilters();
  const availableFilterOptions = availableFilters.reduce(
    (acc, availableFilter) => [
      ...acc,
      { value: availableFilter.key, label: getFilterName(availableFilter?.key) },
    ],
    [],
  );

  return (
    <ItemContainer elevation={1}>
      {!isOpen && !filter.isNew && (
        <Header
          onClick={handleLoadFilterSelection}
          onKeyPress={handleLoadFilterSelection}
          role="button"
          tabIndex="0"
        >
          {getFilterDisplayValue({
            name: getFilterName(filter.key),
            operator: filter.operator,
            value: filter.value,
            type: getFilterType(filter.key),
          })}
        </Header>
      )}

      {!isOpen && filter.isNew && (
        <HeaderNew
          onClick={handleLoadFilterSelection}
          onKeyPress={handleLoadFilterSelection}
          role="button"
          tabIndex="0"
        >
          <FormattedMessage {...translations.newFilterHeader} />
        </HeaderNew>
      )}

      {isOpen && (
        <Header
          onClick={handleCloseFilterSelection}
          onKeyPress={handleCloseFilterSelection}
          role="button"
          tabIndex="0"
        >
          {getFilterDisplayValue({
            name: getFilterName(filterSelection.key),
            operator: filterSelection.operator,
            value: filterSelection.value,
            type: getFilterType(filterSelection.key),
          })}
          <div style={{ marginLeft: 'auto' }}>
            <IconButton size="small" onClick={handleRemoveFilterSelection}>
              <Icon icon={['fal', 'trash-alt']} size="small" />
            </IconButton>
          </div>
        </Header>
      )}

      <Collapse in={isOpen}>
        {filterSelection && (
          <form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box px={1}>
              <Filter
                filter={{ ...filterSelection, values: filterSelection?.value }}
                filterOptions={availableFilterOptions}
                margin="dense"
                onFetchOperators={handleFetchOperators}
                onFetchFormat={handleFetchFormat}
                onFetchSuggestions={handleFetchSuggestions}
                onFetchValueProps={handleFetchValueProps}
                onChange={handleChangeSelection}
                formatters={filterFormatters}
              />
            </Box>
            <Actions>
              <Button variant="outlined" onClick={handleCloseFilterSelection}>
                <FormattedMessage {...translations.cancelButton} />
              </Button>
              <Button variant="contained" type="submit" color="primary">
                <FormattedMessage {...translations[filter.isNew ? 'createButton' : 'saveButton']} />
              </Button>
            </Actions>
          </form>
        )}
      </Collapse>
    </ItemContainer>
  );
};

FilterSelectionItem.propTypes = {
  filter: PropTypes.object.isRequired,
  filterFormatters: PropTypes.object,
  filterRenderers: PropTypes.object,
  isOpen: PropTypes.bool,
};

FilterSelectionItem.defaultProps = {
  filterFormatters: {},
  filterRenderers: {},
  isOpen: false,
};

export default FilterSelectionItem;
